import { Component, createRef } from 'react';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChrFile } from "./nes/ChrFile";
import { v4 } from "uuid";
import GameplayStyle from "../data/GameplayStyle";
import gameData from "../mobx/GameData";
import ApplicationState from "../mobx/ApplicationState";

import "./NewGameDialog.scss";
import GraphicsPatcher from "./services/GraphicsPatcher";

const allFields = ['gameTitle', 'author', 'year', 'includeExampleLevels', 'builtInTileId', 'builtInSpriteId',];

export default class NewGameDialog extends Component {
    form = null;
    BuiltInTilesets = [];
    BuiltInSprites = [];
    currentTileset = {};
    currentTilesetPalettes = {};

    constructor(props) {
        super(props);

        this.state = {
            gameData: {},
            modalVisible: false,
            formSubmittedOnce: false,
            builtInSpriteId: 0,
            builtInTileId: 0,
            loading: true
        }
        this.form = createRef();
    }

    async componentDidMount() {
        const tiles = await import('../data/BuiltInTilesets');
        const sprites = await import('../data/BuiltInSprites');
        this.BuiltInTilesets = tiles.default;
        this.BuiltInSprites = sprites.default;
        this.rebuildCurrentTileset();
        // Avoid trying to render the dialog until we have all tilesets/etc data available
        this.setState({loading: false});
    }

    rebuildCurrentTileset() {
        this.currentTileset = this.BuiltInTilesets.find(t => t.id === this.state.builtInTileId);
        this.currentTilesetPalettes = [
            this.currentTileset.palette.slice(0, 4),
            this.currentTileset.palette.slice(4, 8),
            this.currentTileset.palette.slice(8, 12),
            this.currentTileset.palette.slice(12, 16)
        ];
        this.forceUpdate();
    }

    getFeedbackData(field) {
        const fieldElem = document.getElementById(field);
        if (!fieldElem || !this.state.formSubmittedOnce) {
            return {valid: 'indeterminate', feedback: ''};
        }
        const fieldValue = fieldElem.value;

        switch(field) {

            case 'gameTitle':
                if (fieldValue.length === 0) {
                    return {valid: false, feedback: 'A value is required'};
                } else if (fieldValue.length > 26) {
                    return {valid: false, feedback: 'Game title cannot be more than 26 characters. (' + fieldValue.length + ')'};
                } else if (gameData.userGames.find(a => a.title.toLowerCase() === fieldValue.toLowerCase())) {
                    return { valid: false, feedback: 'Cannot have two games with the same title!'};
                }
                break;
            case 'author':
                if (fieldValue.length === 0) {
                    return {valid: false, feedback: 'A value is required'};
                } else if (fieldValue.length > 64) {
                    return {valid: false, feedback: 'Author name cannot be longer than 64 characters'};
                }
                break;
            default:
        }

        // If you make it out, we don't care about your field. Carry on.
        return {valid: true, feedback: ''};
    }

    async fieldChanged() {

        this.setState({
            builtInTileId: parseInt(document.getElementById('builtInTileId').value, 10),
            builtInSpriteId: parseInt(document.getElementById('builtInSpriteId').value, 10)
        }, () => this.rebuildCurrentTileset());
    }

    async saveNewGame(event) {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        this.setState({saving: true})

        // Need this field to be updated before we can do other calculations.
        await new Promise(resolve => this.setState({formSubmittedOnce: true}, resolve));

        let isValid = true;
        allFields.forEach(field => {
            if (!this.getFeedbackData(field).valid) {
                isValid = false;
            }
        });
        if (!isValid) {
            console.warn('One or more fields is invalid, bailing out of game creation.');
            this.setState({saving: false});
            return;
        }

        const tilesetId = parseInt(document.getElementById('builtInTileId').value, 10);
        const spriteId = parseInt(document.getElementById('builtInSpriteId').value, 10);

        const {default: BaseGame} = await import('../data/BaseGame');

        let newGame = BaseGame(v4(), {
            title: document.getElementById('gameTitle').value,
            author: document.getElementById('author').value,
            useBuiltInGraphics: true,
            builtInTileId: tilesetId,
            builtInSpriteId: spriteId,
            defaultLevelStyle: parseInt(document.getElementById('gameplayStyle').value, 10),
            gamePaletteData: this.BuiltInTilesets.find(a => a.id === tilesetId).palette,
            spritePalette: this.BuiltInSprites.find(a => a.id === spriteId).palette,
            tileData: this.BuiltInTilesets.find(a => a.id === tilesetId).tileData
        });

        newGame.gameGraphicsData = await GraphicsPatcher.getPatchedGraphicsDataForGame(newGame);

        if (document.getElementById('includeExampleLevels').checked) {
            newGame.lastLevel = 7;
            const { getDemoLevelsForGame } = await import('../data/DemoLevels');
            const theLevels = getDemoLevelsForGame(newGame);
            for (let i = 0; i < theLevels.length; i++) {
                newGame.levels[i] = theLevels[i];
            }
        }

        newGame.titleScreenData = await GraphicsPatcher.getPatchedTitleScreenDataForGame(newGame);
        newGame.creditsScreenData = await GraphicsPatcher.getPatchedCreditsDataForGame(newGame);

        gameData.addGame(newGame);
        gameData.setCurrentGame(newGame.id);

        // SUCCESS! Send the user elsewhere and close the dialog.
        ApplicationState.addAlert('success', 'Game created successfully!')
        this.setState({modalVisible: false});
    }


    render() {
        // Once we're done, trigger a redirect to the level page. (Yes, this is really how react-router wants us to do this. Meh, fine.)
        if (this.state.loading) { return <></>; }
        return <>
            <Button variant="primary" onClick={() => this.setState({modalVisible: true})}>New Game</Button>
            <Modal className="new-game-dialog" size="lg" centered={true} backdrop="static" show={this.state.modalVisible} onHide={() => this.setState({modalVisible: false})} restoreFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Create a New Game</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Use the form below to start building your game! Everything you write here can be changed later, so don't stress over it too much.</p>
                    <Form noValidate ref={this.form}>
                        <Form.Group className="mb-3" controlId="gameTitle">
                            <Form.Label>Game Title</Form.Label>
                            <Form.Control required isValid={this.getFeedbackData('gameTitle').valid === true} isInvalid={this.getFeedbackData('gameTitle').valid === false} type="text" placeholder="Enter a game title!" onChange={() => this.forceUpdate()} />
                            <Form.Control.Feedback type="invalid">{this.getFeedbackData('gameTitle').feedback}</Form.Control.Feedback>
                            <Form.Text as="small" className="text-muted">Max 26 characters</Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="author">
                            <Form.Label>Author</Form.Label>
                            <Form.Control required isValid={this.getFeedbackData('author').valid === true} isInvalid={this.getFeedbackData('author').valid === false} type="text" placeholder="Enter your name!" onChange={() => this.forceUpdate()} />
                            <Form.Control.Feedback type="invalid">{this.getFeedbackData('author').feedback}</Form.Control.Feedback>
                            <Form.Text as="small" className="text-muted">Names longer than 26 characters may appear unusually</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="builtInSpriteId">Player Sprite</Form.Label>
                            <Form.Select arial-label="Select a sprite" onChange={() => this.fieldChanged()} defaultValue={this.state.builtInSpriteId} id="builtInSpriteId">
                                {this.BuiltInSprites.map(opt => {
                                    return <option key={opt.id} value={opt.id}>{opt.title}</option>
                                })}
                            </Form.Select>
                            <strong>Preview</strong>
                            <div className="chr-scissors sm">
                                <ChrFile data={this.BuiltInSprites.find(t => t.id === this.state.builtInSpriteId).data} palette={this.BuiltInSprites.find(t => t.id === this.state.builtInSpriteId).palette} />
                            </div>
                        </Form.Group>


                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="builtInTileId">Game Tiles</Form.Label>
                            <Form.Select arial-label="Select a tileset" onChange={() => this.fieldChanged()} defaultValue={this.state.builtInTileId} id="builtInTileId">
                                {this.BuiltInTilesets.map(opt => {
                                    return <option key={opt.id} value={opt.id}>{opt.title}</option>
                                })}
                            </Form.Select>
                            <div className="mt-2 mb-1">Preview</div>
                            <div className="tile-holder">
                                {this.currentTileset.tileData.map((tileDat, idx) => (
                                    <ChrFile key={'tile'+idx} data={this.currentTileset.data} palette={this.currentTilesetPalettes[tileDat.palette]} isMeta={true} tileId={idx} />
                                ))}

                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="gameplayStyle">Default Gameplay Style</Form.Label>
                            <Form.Select arial-label="Select a gameplay style" defaultValue={GameplayStyle.FILL_HOLES_WITH_CRATES} id="gameplayStyle">
                                {Object.keys(GameplayStyle).map(name => {
                                    return <option key={name} value={GameplayStyle[name]}>{name.replace(/_/g, ' ').toLowerCase()}</option>
                                })}
                            </Form.Select>
                        </Form.Group>

                        <OverlayTrigger placement="left" overlay={<Tooltip><p>Checking this box will cause the first 8 levels of your game to be filled with simple example puzzles for you to build from.</p><p>If left unchecked, all levels will be left as a blank slate instead.</p></Tooltip>}>
                            <Form.Group className="mb-3">
                                <Form.Check id="includeExampleLevels" label="Include 8 example levels at start of game" defaultChecked={true} />
                            </Form.Group>
                        </OverlayTrigger>


                        {/* Hidden field to prevent default clicks */}
                        <Button style={{display: "none"}} type="submit" onClick={event => this.saveNewGame(event)}>Submit</Button>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" disabled={this.state.saving} onClick={() => this.saveNewGame()}>Create!</Button>
                </Modal.Footer>
            </Modal>
        </>
    }
}